import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Image, NavBar, Toast } from 'antd-mobile-v5'
import { useHistory, useLocation } from 'react-router-dom'
import { getCardFaceImageUrl } from '../../services/utilities'
import Tag from '../BuyCards/components/ChanghuanTag'
import styles from '../ClubVipChangeEcards/ClubVipChangeEcards.module.css'
import {
  useBindEcard,
  useClubVipFuiouPay,
  useClubVipPaymentStatus,
  useEcards,
  useGetExclusiveEcards,
  useProfile,
} from '../../services/hooks'
import { useClubVipPayment } from '../../services/useClubVipPayment'
import LoadingStatus from '../../components/FormTools/Loading'
import { allEcards } from '../../utils/cert'
import { showToast } from '../../components/common/tools'
import { refreshEcards } from '../../actions'
import QrCodePay from '../../components/QrCodePay/QrCodePay'
import { getPadiWxId } from '../../utils/user'
import { buildEcardImgBindItem, getEncodedId, getParameterByName, UClick } from '../../components/common/utils'
import { CLUB_URL_DOMAIN } from '../../utils/env'

const ClubEcardsEquity = () => {
  const dispatch = useDispatch()
  const { push } = useHistory()
  const { pathname } = useLocation()

  // 获取页面参数
  const pageVipId = useMemo(() => getParameterByName('pageVipId'), [])
  const isLogin = useMemo(() => getParameterByName('isLogin'), [])
  const padiWxId = useMemo(() => getPadiWxId(), [])

  // 状态管理
  const [selectedCerts, setSelectedCerts] = useState([])
  const [selectedImage, setSelectedImage] = useState(0)

  // Hooks
  const { loading: imageLoading, data: cardData } = useGetExclusiveEcards()

  const {
    loading: ecardsLoading,
    data: ecardsData,
    run: getEcards,
  } = useEcards({
    manual: true,
    onSuccess: (data) => {
      dispatch(refreshEcards(data))
    },
  })

  const {
    loading: profileLoading,
    data: profile,
    run: getProfile,
  } = useProfile({
    manual: true,
  })

  const { loading: submitLoading, run: bindEcard } = useBindEcard({
    onSuccess: () => {
      showToast('卡面更换成功！')
    },
    onError: () => {
      Toast.show({
        icon: 'fail',
        content: '卡面更换失败，请稍后重试',
      })
    },
  })

  const { runAsync: fuiouPay } = useClubVipFuiouPay({})
  // 轮询失败时抑制 useRequestCommon 的全局错误 Modal（轮询类请求不弹窗，与 BooksCheckout/AgentElearning 一致）
  const { runAsync: checkFuiouStatus } = useClubVipPaymentStatus({ onError: () => true })

  // 计算属性
  const ecards = useMemo(() => allEcards(ecardsData), [ecardsData])
  const hasChanghuanRights = useMemo(() => profile && profile.hasChangeRight === 2, [profile])
  const currentCard = useMemo(() => cardData && cardData[selectedImage], [cardData, selectedImage])

  // 初始化数据
  useEffect(() => {
    if (padiWxId && !isLogin) {
      Promise.all([getEcards(), getProfile()]).catch((error) => {
        console.error('初始化数据失败:', error)
        Toast.show({
          icon: 'fail',
          content: '加载数据失败，请稍后重试',
        })
      })
    }
  }, [padiWxId, isLogin, getEcards, getProfile])

  // 获取VIP类型
  const getVipType = useCallback(() => {
    switch (pageVipId) {
      case '1':
        return 'CLUB_VIP'
      case '2':
        return 'CLUB_LITE_VIP'
      case '3':
        return 'CLUB_PERIPHERY_VIP'
      default:
        return 'CLUB_LITE_VIP'
    }
  }, [pageVipId])

  // 获取VIP按钮文本
  const getVipButtonText = useCallback(() => {
    switch (pageVipId) {
      case '1':
        return '开通畅享课程会员'
      case '2':
        return '开通畅享卡面会员'
      case '3':
        return '开通畅享周边会员'
      default:
        return '立即开通会员'
    }
  }, [pageVipId])

  // 切换卡面
  const changeImage = useCallback((index) => {
    setSelectedCerts([])
    setSelectedImage(index)
  }, [])

  // 切换证书选择
  const toggleCert = useCallback(
    (ecard) => {
      if (!currentCard) return

      const ecardId = getEncodedId(ecard)
      const hasCurrentCert = selectedCerts.find((cert) => cert.ecardId === ecardId)

      if (!hasCurrentCert) {
        // 本页列的是「独家换面集」（GET /api/ecards/clubVipImageChange）；载荷字段名由 buildEcardImgBindItem 统一
        setSelectedCerts([...selectedCerts, buildEcardImgBindItem(ecardId, currentCard.ecardImageId)])
      } else {
        setSelectedCerts(selectedCerts.filter((c) => c.ecardId !== ecardId))
      }
    },
    [selectedCerts, currentCard]
  )

  const handleBeforePay = useCallback(() => UClick('clickBuySpecialCard'), [])
  const { toPay, nativeQrCode, nativePrice, nativeCurrency, nativePayDisplay, setNativePayDisplay } = useClubVipPayment(
    {
      type: getVipType,
      fuiouPay,
      checkFuiouStatus,
      onBeforePay: handleBeforePay,
    }
  )

  // 登录
  const toLogin = useCallback(() => {
    push(`/emailLogin?referer=${pathname}`)
  }, [push, pathname])

  // 处理绑定卡面：本页列的是「独家换面集」（GET /api/ecards/clubVipImageChange，40/47/51/52/53 + 生肖 VIP
  // 副本）。这些面 Phase 2 起既不落所有权、也不在 level1/level2 可用集里，**只有 POST /api/ecardImage/binding
  // 能装备它们**（VIP 分支：校验 level > 0 后写装备位）。改走 PUT /rebinding 会被可得性判据
  // （已拥有 ∪ level 可用集）静默跳过：接口 200、前端弹「成功」，但卡面没换。
  const handleBindEcard = useCallback(() => {
    if (selectedCerts.length === 0) {
      Toast.show({
        icon: 'warning',
        content: '请选择至少一张证书',
      })
      return
    }
    bindEcard(selectedCerts)
  }, [selectedCerts, bindEcard])

  // 返回处理
  const handleBack = useCallback(() => {
    window.location.href = CLUB_URL_DOMAIN + '/pages-mine/mine/member-center'
  }, [])

  // 加载状态
  if (imageLoading) {
    return <LoadingStatus />
  }

  return (
    <div className={styles.wrapper}>
      <QrCodePay
        visible={nativePayDisplay}
        setVisible={setNativePayDisplay}
        url={nativeQrCode}
        price={nativePrice}
        currency={nativeCurrency}
      />

      {/* 导航栏 */}
      <NavBar back="返回" onBack={handleBack} />

      {/* 卡面预览 */}
      {currentCard && (
        <Image className={styles.card} src={getCardFaceImageUrl(currentCard.imgFile, currentCard.desc)} />
      )}

      {/* 卡面选择 */}
      <h4 className={styles['options-title']}>选择卡面</h4>
      <div className={styles.tags}>
        {cardData.map((card, index) => (
          <Tag key={card.ecardImageId} selected={index === selectedImage} onClick={() => changeImage(index)}>
            {card.desc}
          </Tag>
        ))}
      </div>

      {/* 证书选择 */}
      {padiWxId && (
        <>
          <h4 className={styles['options-title']}>选择证书（可多选）</h4>
          {ecardsLoading ? (
            <LoadingStatus />
          ) : (
            <div className={styles['certs-tags']}>
              {ecards.map((ecard) => (
                <Tag
                  key={ecard.encodedId}
                  selected={!!selectedCerts.find((c) => c.ecardId === getEncodedId(ecard))}
                  onClick={() => toggleCert(ecard)}
                >
                  {ecard.ecardName}
                </Tag>
              ))}
            </div>
          )}
        </>
      )}

      {/* 底部按钮 */}
      <div className={styles.bottom}>
        {!padiWxId || isLogin ? (
          <div className={styles.button} onClick={toLogin}>
            登录识别可使用证书
          </div>
        ) : profileLoading ? null : hasChanghuanRights ? (
          <Button
            block
            className={`${styles.button} ${selectedCerts.length === 0 ? styles['button-disabled'] : ''}`}
            onClick={handleBindEcard}
            disabled={selectedCerts.length === 0}
            loading={submitLoading}
          >
            一键畅换
          </Button>
        ) : (
          <div className={styles.button} onClick={toPay}>
            {getVipButtonText()}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClubEcardsEquity
